import { encodingTriggeredHistoryStatuses } from "@material-dev/insights-portal-ui-components/lib/feature-components/m-update-encoded-data-dialog/const";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import {
  ApiException,
  EncodedDataClient,
  EncodingTriggeredHistoryDto,
  EncodingTriggeringStatuses,
  EncodingVersionDto,
  IEncodedDataClient,
} from "app/SharedApiClient";
import { BASE_SHARED_API_URL } from "const/baseUrl";
import { CustomHttpStatuses } from "const/customHttpStatuses";
import { httpStatus } from "const/httpStatus";
import { IState } from "slices";
import { setGenerateDictionaryDataDialogState } from "slices/projectsData/projectsDataSlice";
import { LoadingState } from "types";

import { originProjectEncodingVersion, ProjectEncodingVersionState } from "./projectEncodingVersionState";

const encodedDataSharedClient: IEncodedDataClient = new EncodedDataClient(BASE_SHARED_API_URL);

export const getEncodingDataTriggeredHistory = createAsyncThunk(
  "projectEncodingVersion/getEncodingDataTriggeredHistory",
  async (projectId: string, { dispatch, rejectWithValue }) => {
    try {
      const result = await encodedDataSharedClient.getEncodingTriggeredHistory(projectId);

      if (result.status === EncodingTriggeringStatuses.InProgress) {
        // if in history it was in progress
        // then we need to retrieve the status again and check
        dispatch(checkAndUpdateTriggeredEncodingGeneration());
      }

      return result;
    } catch (error) {
      ApiException.isApiException(error);

      return rejectWithValue(error);
    }
  },
);

export const getProjectVersions = createAsyncThunk(
  "projectEncodingVersion/getProjectVersions",
  async ({ projectId, source }: { projectId: string; source: string }, { rejectWithValue }) => {
    try {
      const response = await encodedDataSharedClient.getEncodedVersions(projectId, source);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const projectEncodingVersionSlice = createSlice({
  name: "projectEncodingVersion",
  initialState: originProjectEncodingVersion,
  reducers: {
    resetProjectEncodingVersion: () => originProjectEncodingVersion,
    resetEncodingProgressStage: (state) => {
      state.encodingProgressStage = undefined;
      state.encodingLastUpdateReceived = undefined;
    },
    setTriggeredEncodingGeneration: (state, { payload }: PayloadAction<EncodingTriggeredHistoryDto | null>) => {
      state.encodingTriggeredHistory = payload;
    },
    setLatestVersionLoadingState: (state, { payload }: PayloadAction<LoadingState>) => {
      state.loading = payload;
    },
    setLatestVersion: (state, { payload }: PayloadAction<EncodingVersionDto | null>) => {
      state.latestEncodingVersion = payload;
    },
    setEncodingProgressStage: (state, { payload }: PayloadAction<string | undefined>) => {
      state.encodingProgressStage = payload;
    },
    setEncodingLastUpdateReceived: (state, { payload }: PayloadAction<string | undefined>) => {
      state.encodingLastUpdateReceived = payload;
    },
  },
  extraReducers: (builder) => {
    // getEncodingDataTriggeredHistory
    builder.addCase(getEncodingDataTriggeredHistory.pending, (state) => {
      state.encodingTriggeredHistoryLoading = LoadingState.pending;
    });
    builder.addCase(getEncodingDataTriggeredHistory.fulfilled, (state, { payload }) => {
      state.encodingTriggeredHistoryLoading = LoadingState.succeed;

      if (payload.status !== EncodingTriggeringStatuses.Complete) {
        state.encodingTriggeredHistory = payload;
        !!payload.errorMessage && (state.encodingTriggeredErrorMessage = payload.errorMessage);
      }
    });
    builder.addCase(getEncodingDataTriggeredHistory.rejected, (state) => {
      // TODO: it will be rejected and passed to here even if 204 was returned. Rework MSharedApi to fix
      state.encodingTriggeredHistoryLoading = LoadingState.failed;
    });

    // getProjectVersions
    builder.addCase(getProjectVersions.pending, (state) => {
      state.availableVersions = [];
      state.availableVersionsLoading = LoadingState.pending;
    });
    builder.addCase(getProjectVersions.fulfilled, (state, { payload }) => {
      state.availableVersions = payload;
      state.availableVersionsLoading = LoadingState.succeed;
    });
    builder.addCase(getProjectVersions.rejected, (state) => {
      state.availableVersions = [];
      state.availableVersionsLoading = LoadingState.failed;
    });
  },
});

export const {
  resetProjectEncodingVersion,
  setTriggeredEncodingGeneration,
  setLatestVersionLoadingState,
  setLatestVersion,
  setEncodingProgressStage,
  setEncodingLastUpdateReceived,
  resetEncodingProgressStage
} = projectEncodingVersionSlice.actions;

export const projectEncodingVersionSelector = (state: RootState): ProjectEncodingVersionState =>
  state.projectEncodingVersion;

export const generateEncodingData = createAsyncThunk(
  "projectEncodingVersion/generateEncodingData",
  async ({includeNonComplete, stackedVariableName, isStacked}:{includeNonComplete: boolean, stackedVariableName?: string, isStacked?: boolean}, { getState, dispatch, signal, rejectWithValue }) => {
    try {
      const { currentProject } = (getState() as IState).project;

      if (!currentProject?.originProjectId) {
        throw Error("Dataset required");
      }
      dispatch(setLatestVersion(null));
      dispatch(setGenerateDictionaryDataDialogState(true));
      const triggeredEncodingGeneration = await encodedDataSharedClient.generateDataDictionary(
        currentProject.originProjectId,
        String(currentProject.resultSourceName),
        includeNonComplete,
        stackedVariableName,
        false,
        isStacked
      );
      if (signal.aborted) {
        return;
      }
      dispatch(setTriggeredEncodingGeneration(triggeredEncodingGeneration));
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getProjectLatestVersion = createAsyncThunk(
  "projectEncodingVersion/getProjectLatestVersion",
  async (_, { dispatch, getState }) => {
    const { currentProject } = (getState() as IState).project;

    if (!currentProject?.originProjectId) {
      throw Error("Origin dataset id required");
    }
    try {
      dispatch(setLatestVersionLoadingState(LoadingState.pending));
      const response = await encodedDataSharedClient.getLatestEncodingVersion(
        currentProject.originProjectId,
        String(currentProject.resultSourceName),
      );
      dispatch(setLatestVersionLoadingState(LoadingState.succeed));
      dispatch(setLatestVersion(response));
      return response.versionId;
    } catch (error) {
      if (ApiException.isApiException(error)) {
        const { message: generalMessage, response, status } = error;
        const { errorMessage } = JSON.parse(response) as { errorMessage: string };
        if (httpStatus.notFound === status) {
          // it is okay if project doesn't have version so far
          // no need to set status as failed
          dispatch(setLatestVersionLoadingState(LoadingState.notFound));
        } else {
          console.error(errorMessage || generalMessage || error);
          dispatch(setLatestVersionLoadingState(LoadingState.failed));
        }
      } else {
        dispatch(setLatestVersionLoadingState(LoadingState.failed));
      }
    }
  },
);

export const checkAndUpdateTriggeredEncodingGeneration = createAsyncThunk(
  "projectEncodingVersion/checkAndUpdateTriggeredEncodingGeneration",
  async (_, { dispatch, getState, signal }) => {
    const { encodingTriggeredHistory, encodingProgressStage } = (getState() as IState)
      .projectEncodingVersion;
    const { currentProject } = (getState() as IState).project;

    const { token, status: currentStatus, createdAt, useDatalakeApi, isStacked } = encodingTriggeredHistory || {};

    if (!token) {
      return;
    }

    try {
      // get status by token
      const statusDetails = await encodedDataSharedClient.getEncodingTriggeringStatusDetails(
        currentProject?.resultSourceName,
        token,
        createdAt,
        useDatalakeApi,
        isStacked
      );
      const status = statusDetails?.status;
      dispatch(setEncodingLastUpdateReceived(statusDetails.lastUpdateReceived));
      if (signal.aborted) {
        return;
      }

      if (encodingProgressStage !== statusDetails?.stage) {
        dispatch(setEncodingProgressStage(statusDetails?.stage));
      }

      if (currentStatus !== status) {
        // update status
        const result = await encodedDataSharedClient.updateStatus({
          status,
          token,
          errorMessage: statusDetails?.message,
        });
        if (signal.aborted) {
          return;
        }
        dispatch(setTriggeredEncodingGeneration(result));
      }
      if (statusDetails.status === encodingTriggeredHistoryStatuses.complete && currentProject?.originProjectId) {
        void dispatch(getProjectLatestVersion());
        await dispatch(
          getProjectVersions({
            projectId: currentProject?.originProjectId,
            source: String(currentProject.resultSourceName),
          }),
        );
      }
    } catch (error) {
      if (ApiException.isApiException(error)) {
        const { status } = error;
        if (status === CustomHttpStatuses.integrationApiError) {
          const result = await encodedDataSharedClient.updateStatus({
            status: EncodingTriggeringStatuses.Failed,
            token,
            errorMessage: error.message,
          });
          dispatch(setTriggeredEncodingGeneration(result));
        }
      }
    }
  },
);

export default projectEncodingVersionSlice.reducer;
